// InterviewerView.js
import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from './CustomAlert/AlertContext';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SIGNALING_SERVER, API_BASE, getAiInterviewScreenshotIntervalMs } from '../utils/config';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import * as LivekitClient from 'livekit-client';
import useScreenshotCapture from '../hooks/useScreenshotCapture';
import LinkExpiredView from './LinkExpiredView';
import alertLogo from '../assets/alert-logo-robot.png';
import CollaborativeWhiteboard from './interview/CollaborativeWhiteboard';
import CollaborativeIDE from './interview/CollaborativeIDE';
import PlaybackViewer from './interview/PlaybackViewer';
import { trackEvent, setUserProperties } from '../utils/analytics';

// ---------- Helpers for localStorage persistence ----------
const loadState = (key, defaultValue) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : defaultValue;
  } catch (e) { return defaultValue; }
};
const saveState = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { }
};

// ============================================================
// 🎨 Shared design tokens — keeps every surface in this view
// pulling from the same type scale + accent palette.
// ============================================================
const FONT_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const ACCENT = {
  mic: '#2563eb',
  whiteboard: '#7c3aed',
  ide: '#16a34a',
  replay: '#d97706',
  danger: '#dc2626',
  info: '#2563eb',
};

const GlobalFontImport = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>
);

// ============================================================
// ✅ Custom Confirm Modal Component (replaces window.confirm)
// ============================================================
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'var(--bg-overlay)', backdropFilter: 'blur(6px)', zIndex: 99999,
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    animation: 'fadeIn 0.2s ease-out',
  }}>
    <GlobalFontImport />
    <div style={{
      background: 'var(--bg-card)', borderRadius: '20px', padding: '40px 36px',
      maxWidth: '420px', width: '90%',
      boxShadow: '0 24px 70px rgba(15,23,42,0.28), 0 0 0 1px rgba(255,255,255,0.04)',
      textAlign: 'center', fontFamily: FONT_STACK,
      animation: 'modalPop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }}>
      <div style={{
        width: '88px', height: '88px', borderRadius: '50%', margin: '0 auto 18px',
        background: 'linear-gradient(145deg, rgba(220,38,38,0.12), rgba(220,38,38,0.04))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid rgba(220,38,38,0.15)',
      }}>
        <img
          src={alertLogo}
          alt="Warning"
          style={{ width: '60px', height: '60px', objectFit: 'contain' }}
        />
      </div>
      <h3 style={{ fontSize: '19px', color: 'var(--text-primary)', marginBottom: '10px', fontWeight: '700', letterSpacing: '-0.01em' }}>
        End Interview
      </h3>
      <p style={{ fontSize: '14.5px', color: 'var(--text-tertiary)', marginBottom: '30px', lineHeight: 1.6 }}>
        {message}
      </p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button onClick={onCancel} style={{
          padding: '12px 28px', borderRadius: '12px', border: '1px solid var(--border-color)',
          background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: '14px',
          fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: FONT_STACK,
        }}>
          Cancel
        </button>
        <button onClick={onConfirm} style={{
          padding: '12px 28px', borderRadius: '12px', border: 'none',
          background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', fontSize: '14px',
          fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s ease', fontFamily: FONT_STACK,
          boxShadow: '0 8px 20px rgba(220,38,38,0.35)',
        }}>
          Yes, End Interview
        </button>
      </div>
    </div>
    <style>{`
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes modalPop { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
    `}</style>
  </div>
);

// ============================================================
// ✅ Full Screen Loader Overlay Component
// ============================================================
const LoadingOverlay = () => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'var(--bg-overlay-light)', backdropFilter: 'blur(4px)', zIndex: 99998,
    display: 'flex', flexDirection: 'column',
    justifyContent: 'center', alignItems: 'center',
    fontFamily: FONT_STACK,
    animation: 'fadeIn 0.2s ease-out',
  }}>
    <GlobalFontImport />
    {/* Spinner */}
    <div style={{
      width: '58px', height: '58px', borderRadius: '50%',
      border: '4px solid var(--border-color)',
      borderTop: '4px solid #2563eb',
      borderRight: '4px solid #7c3aed',
      animation: 'spin 0.85s cubic-bezier(0.5, 0, 0.5, 1) infinite',
      marginBottom: '26px',
    }} />
    <h2 style={{
      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
      WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
      marginBottom: '8px', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.01em',
    }}>
      ✨ Ending Interview...
    </h2>
    <p style={{ color: 'var(--text-muted)', fontSize: '14.5px', maxWidth: '320px', textAlign: 'center', lineHeight: 1.6 }}>
      AI is cleaning and structuring the transcript. Please wait a few seconds!
    </p>
    <style>{`
      @keyframes spin {
        0%   { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    `}</style>
  </div>
);

// ============================================================
// Main InterviewerView Component
// ============================================================
const InterviewerView = ({ roomId, userName }) => {
  const { showAlert, showConfirm } = useAlert();

  const navigate = useNavigate();
  const [interviewerName, setInterviewerName] = useState('');
  const [interviewerEmail, setInterviewerEmail] = useState('');
  const [step, setStep] = useState('nameEntry'); // 'nameEntry' or 'meeting'
  const [isCleaning, setIsCleaning] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // ✅ NEW
  const [isCheckingLink, setIsCheckingLink] = useState(true);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // ✅ GMeet Sidebar state

  // ====== COLLABORATIVE FEATURES STATE ======
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showIDE, setShowIDE] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'whiteboard' | 'ide' | 'playback' | null
  const activePanelRef = useRef(null); // Mirror of activePanel for stale-closure-safe reads

  // Toggle panel — syncs state across clients so opening on one side opens on the other
  const togglePanel = (panel, isFromRemote = false) => {
    // Use ref to avoid stale closure bug when called from socket listeners registered at mount
    let targetPanel = activePanelRef.current === panel ? null : panel;
    
    // If coming from remote command, align state exactly to payload
    if (isFromRemote) {
      targetPanel = panel; // can be 'whiteboard', 'ide', 'playback' or null
    }

    activePanelRef.current = targetPanel; // Keep ref in sync
    setActivePanel(targetPanel);
    setShowWhiteboard(targetPanel === 'whiteboard');
    setShowIDE(targetPanel === 'ide');
    setShowPlayback(targetPanel === 'playback');

    // Emit UI state to candidate — but NEVER sync 'playback' (interviewer-only feature)
    if (!isFromRemote && socketRef.current && targetPanel !== 'playback') {
      socketRef.current.emit('sync-panel', { panel: targetPanel });
    }
  };

  // ---------- Persistent states ----------
  const [connectionStatus, setConnectionStatus] = useState(
    loadState('interviewer_connectionStatus', 'Initializing...')
  );

  const [alerts, setAlerts] = useState(() => {
    const lastRoomId = localStorage.getItem('interviewer_last_room_id');
    if (lastRoomId !== roomId) {
      console.log('🧹 New session detected! Clearing previous alerts.');
      localStorage.removeItem('interviewer_alerts');
      localStorage.setItem('interviewer_last_room_id', roomId);
      return [];
    }
    return loadState('interviewer_alerts', []);
  });

  const [candidateConnected, setCandidateConnected] = useState(false);
  const [remoteInterviewers, setRemoteInterviewers] = useState([]);
  const [candidateScreenShareTrack, setCandidateScreenShareTrack] = useState(null);
  const [candidateParticipantName, setCandidateParticipantName] = useState('');
  const [candidateIdentity, setCandidateIdentity] = useState(null);
  const [candidateParticipant, setCandidateParticipant] = useState(null);

  useEffect(() => saveState('interviewer_connectionStatus', connectionStatus), [connectionStatus]);
  useEffect(() => saveState('interviewer_alerts', alerts), [alerts]);

  // ---------- Refs ----------
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const livekitRoomRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenshotStartedRef = useRef(false);
  const hasRedirectedToResultsRef = useRef(false);
  const cancelledRef = useRef(false); // guards async initializeConnection from running after cleanup
  const candidateVideoTrackRef = useRef(null); // stores candidate video track so we can attach after DOM renders

  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [mutedParticipants, setMutedParticipants] = useState(new Set());

  const toggleMic = async () => {
    if (livekitRoomRef.current && livekitRoomRef.current.localParticipant) {
      const newState = !isMicEnabled;
      await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(newState);
      setIsMicEnabled(newState);
      if (newState) {
        startSpeechRecognition();
      } else {
        stopSpeechRecognition();
      }
    }
  };

  const {
    startCapture: startScreenshotCapture,
    captureOnEnd: captureScreenshotOnEnd,
  } = useScreenshotCapture({
    interviewId: roomId,
    userId: interviewerName || userName || 'interviewer',
    role: 'interviewer',
    captureInterval: getAiInterviewScreenshotIntervalMs(),
    maxWidth: 1920,
    quality: 0.7,
    maxCaptures: Infinity,
    enabled: true,
    addWatermark: true,
    captureMode: 'viewport',
  });

  // Keep track if component is mounted to avoid state updates after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      console.log('🧹 Interviewer cleaning up on unmount...');
      cleanupConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  useEffect(() => {
    const checkRoomStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/status`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'COMPLETED') {
            console.warn('⚠️ Interview link expired for interviewer: room already completed.');
            setIsLinkExpired(true);
          }
        } else if (res.status === 404) {
          setIsLinkExpired(true);
        }
      } catch (error) {
        console.warn('⚠️ Failed to verify interviewer link status. Allowing entry fallback.', error);
      } finally {
        setIsCheckingLink(false);
      }
    };

    checkRoomStatus();
  }, [roomId]);

  useEffect(() => {
    if (step !== 'meeting' || !interviewerName.trim()) return;

    // cancelled flag prevents async initializeConnection from continuing
    // if this effect's cleanup fires before it completes (React Strict Mode / deps change)
    let cancelled = false;
    cancelledRef.current = false;

    initializeConnection(cancelled);

    return () => {
      cancelled = true;
      cancelledRef.current = true;
      cleanupConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, interviewerName]);

  // ==================== PREVENT REFRESH ====================
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if ((e.key === 'F5') || (e.ctrlKey && e.key.toLowerCase() === 'r') || (e.metaKey && e.key.toLowerCase() === 'r')) {
        if (step === 'meeting') {
          e.preventDefault();
          const confirmed = await showConfirm('Are you sure you want to reload? The interview is in progress.');
          if (confirmed) {
            window.location.reload();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, showConfirm]);

  useEffect(() => {
    if (step === 'meeting') {
      const handleBeforeUnload = (e) => {
        const message = "Are you sure you want to leave? The interview is in progress.";
        e.preventDefault();
        e.returnValue = message;
        return message;
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [step]);

  const handleNameSubmit = async () => {
    if (!interviewerName.trim()) {
      showAlert('Please enter your name', 'error');
      return;
    }
    await saveInterviewerName();
    setStep('meeting');
  };

  // ---------- Speech Recognition ----------
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const [isRecording, setIsRecording] = useState(false);
  const [secondsSinceLastSpeech, setSecondsSinceLastSpeech] = useState(0);
  const [lastSpeechTime, setLastSpeechTime] = useState(Date.now());
  const pauseTimerRef = useRef(null);
  const lastTranscriptRef = useRef('');
  const PAUSE_THRESHOLD = 10000;
  const speechStartedRef = useRef(false);

  const redirectToResultsOnce = () => {
    if (hasRedirectedToResultsRef.current) return;
    hasRedirectedToResultsRef.current = true;
    navigate(`/results/${roomId}?viewer=interviewer`, { replace: true });
  };
  const transcriptRef = useRef(transcript);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  useEffect(() => {
    if (step === 'meeting' && browserSupportsSpeechRecognition && !speechStartedRef.current) {
      console.log('✅ Auto-starting speech recognition...');
      setTimeout(() => {
        startSpeechRecognition();
        speechStartedRef.current = true;
      }, 2000);
    }
  }, [step, browserSupportsSpeechRecognition]);

  useEffect(() => {
    console.log('👨‍💼 InterviewerView MOUNTED - Room ID:', roomId, 'User:', userName || 'Guest');
  }, [roomId, userName]);

  const startSpeechRecognition = () => {
    setIsRecording(true);
    setLastSpeechTime(Date.now());
    setSecondsSinceLastSpeech(0);
    SpeechRecognition.startListening({ continuous: true, language: 'en-US', interimResults: true });
    console.log('🎤 Interviewer speech recognition started (continuing session)');
  };

  const stopSpeechRecognition = () => {
    setIsRecording(false);
    SpeechRecognition.stopListening();
    console.log('🛑 Interviewer speech recognition manually stopped');
    const fullText = transcriptRef.current;
    if (!fullText) return;
    const lastLength = lastTranscriptRef.current.length;
    if (fullText.length < lastLength) { lastTranscriptRef.current = ''; }
    const newText = fullText.slice(lastTranscriptRef.current.length).trim();
    if (newText.length > 0) {
      console.log('💾 Saving pending interviewer question on stop...');
      saveInterviewerQuestion(newText);
      lastTranscriptRef.current = fullText;
    }
  };

  const toggleSpeechRecognition = () => {
    if (isRecording) stopSpeechRecognition(); else startSpeechRecognition();
  };

  useEffect(() => {
    if (isRecording && !listening) {
      console.warn('⚠️ Speech recognition stopped unexpectedly. Resuming...');
      SpeechRecognition.startListening({
        continuous: true, language: 'en-US',
        interimResults: true, clearTranscriptOnListen: false,
      });
    }
  }, [listening, isRecording]);

  useEffect(() => {
    if (!listening) return;
    const intervalMinutes = parseInt(process.env.REACT_APP_SPEECH_SAVE_INTERVAL_MINUTES || '1');
    const intervalMs = intervalMinutes * 60 * 1000;
    console.log(`⏱️ Interviewer batch save timer started: ${intervalMinutes} min`);
    const interval = setInterval(() => {
      const fullText = transcriptRef.current;
      if (!fullText) return;
      const lastLength = lastTranscriptRef.current.length;
      if (fullText.length < lastLength) {
        console.warn('⚠️ Transcript reset detected. Resetting cursor.');
        lastTranscriptRef.current = '';
      }
      const newText = fullText.slice(lastTranscriptRef.current.length).trim();
      if (newText.length > 0) {
        console.log(`⏳ ${intervalMinutes} min chunk detected. Saving to DB...`);
        saveInterviewerQuestion(newText);
        lastTranscriptRef.current = fullText;
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [listening]);

  useEffect(() => {
    if (!listening) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastSpeechTime) / 1000);
      setSecondsSinceLastSpeech(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [listening, lastSpeechTime]);

  // ---------- API Calls ----------
  const saveInterviewerName = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/auth/rooms/update-interviewer-name`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId,
            interviewerName: (interviewerName || '').trim(),
            interviewerEmail: (interviewerEmail || '').trim()
          }),
        }
      );
      const data = await response.json();
      if (response.ok) console.log('💾 Interviewer name saved:', data.message);
      else console.error('❌ Failed to save interviewer name:', data.error);
    } catch (error) { console.error('❌ Network error saving interviewer name:', error); }
  };

  const saveInterviewerQuestion = async (questionText) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/questions/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, interviewerName, questionText }),
      });
      const data = await response.json();
      if (response.ok) {
        console.log('❓ Question saved:', data.questionId);
        await fetch(`${API_BASE}/api/conversation/generate-transcript`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId }),
        })
          .then(res => { console.log('✅ Transcript API:', res.status); return res.json(); })
          .then(d => console.log('📝 Transcript Data:', d))
          .catch(err => console.error('❌ Transcript API Error:', err));
        console.log('📝 Full transcript updated');
      }
    } catch (error) { console.error('❌ Network error saving question:', error); }
  };

  // ✅ Step 1: Show custom confirm modal
  const handleEndInterviewClick = () => {
    setShowConfirmModal(true);
  };

  // ✅ Step 2: User clicked Cancel
  const handleConfirmCancel = () => {
    setShowConfirmModal(false);
  };

  // ✅ Step 3: User clicked "Yes, End Interview" → hide modal → show loader → call API
  const handleConfirmEnd = async () => {
    setShowConfirmModal(false);  // Close modal first
    setIsCleaning(true);         // Then show full-screen loader
    await markInterviewComplete();
  };

  const markInterviewComplete = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) {
        setIsCleaning(false);
        console.error('❌ Failed to mark interview complete:', data.error);
        showAlert(data.error || 'Failed to complete interview', 'error');
        return;
      }

      console.log('🧹 Triggering AI transcript cleanup...');
      try {
        await fetch(`${API_BASE}/api/auth/interviews/${roomId}/clean-transcript`, { method: 'POST' });
        console.log('✨ Auto-generating AI Report...');
        await fetch(`${API_BASE}/api/auth/interviews/${roomId}/report`, { method: 'POST' });
        trackEvent('ai_report_generated', { room_id: roomId });
      } catch (err) {
        console.error('⚠️ Transcript cleanup or report failed but interview ended.', err);
      }

      console.log('✅ Interview marked as COMPLETED and cleaned');
      trackEvent('interview_completed', { room_id: roomId });

      if (socketRef.current) {
        socketRef.current.emit('end-interview', { roomId });
      }

      cleanupConnection();

      // Redirect to results page
      redirectToResultsOnce();

    } catch (err) {
      setIsCleaning(false);
      console.error('❌ Network error completing interview:', err);
      showAlert('Network error while completing interview', 'error');
    }
  };

  // ---------- Initialize socket + media ----------
  const initializeConnection = async (cancelled = false) => {
    try {
      console.log('🔌 [INTERVIEWER] Initializing connection for room:', roomId);
      setConnectionStatus('Initializing...');

      const socket = io(SIGNALING_SERVER, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('✅ [INTERVIEWER] Socket connected:', socket.id);
        setConnectionStatus('Connected to Signaling');
        socket.emit('join-room', { roomId, role: 'interviewer', userName: interviewerName });
      });

      socket.on('room-joined', (data) => {
        console.log('✅ [INTERVIEWER] room-joined event:', data);
        setUserProperties({ user_type: 'Interviewer' });
        trackEvent('join_room', { role: 'interviewer', room_id: roomId });
        if (data.candidateConnected) { setCandidateConnected(true); }
        else { setCandidateConnected(false); setConnectionStatus('Waiting for candidate...'); }
      });

      socket.on('peer-joined', (data) => {
        console.log('✅ [INTERVIEWER] peer-joined:', data.role, data.userName);
        if (data.role === 'candidate') setCandidateConnected(true);
      });

      socket.on('peer-left', (data) => {
        console.log('❌ [INTERVIEWER] peer-left:', data.role);
        if (data.role === 'candidate') {
          setCandidateConnected(false);
          setConnectionStatus('Waiting for candidate...');
        }
      });

      socket.on('interview-ended', async () => {
        console.log('🛑 [INTERVIEWER] Interview ended signal received.');
        setIsCleaning(true);
        try {
          await captureScreenshotOnEnd();
        } catch (error) {
          console.warn('⚠️ Final screenshot capture failed:', error);
        }
        cleanupConnection();
        redirectToResultsOnce();
      });

      socket.on('alert', async (alertData) => {
        console.log('🚨 [INTERVIEWER] ALERT RECEIVED:', alertData.type);
        
        if (alertData.type === 'REQUEST_END_INTERVIEW') {
           const confirmed = await showConfirm(`${alertData.message} Do you want to close the interview?`);
           if (confirmed) {
             setIsCleaning(true);
             await markInterviewComplete();
           } else {
             socket.emit('alert', {
               roomId,
               type: 'DECLINE_END_INTERVIEW',
               message: 'Interviewer declined the end request.',
               severity: 'info',
               timestamp: new Date().toISOString()
             });
           }
           return;
        }
        
        const newAlerts = [];
        const timestamp = new Date().toISOString();

        if (alertData.type === 'AI_DETECTION_RESULT' && alertData.aiData?.suggestedQuestion) {
          newAlerts.push({
            id: Date.now() + Math.floor(Math.random() * 1000) + '_sugg',
            type: 'AI_SUGGESTION',
            message: 'Next question suggestion based on response.',
            severity: 'info',
            timestamp,
            suggestedQuestion: alertData.aiData.suggestedQuestion,
          });

          const { suggestedQuestion, ...aiDataWithoutSuggestion } = alertData.aiData;
          newAlerts.push({
            ...alertData,
            id: Date.now() + Math.floor(Math.random() * 1000) + '_score',
            timestamp,
            aiData: aiDataWithoutSuggestion
          });
        } else {
          newAlerts.push({
            ...alertData,
            id: Date.now() + Math.floor(Math.random() * 1000),
            timestamp,
          });
        }

        setAlerts((prev) => [...newAlerts, ...prev]);
      });

      socket.on('disconnect', (reason) => {
        console.warn('⚠️ [INTERVIEWER] Socket disconnected:', reason);
        setConnectionStatus('Disconnected: ' + reason);
        setCandidateConnected(false);
      });

      socket.on('connect_error', (err) => {
        console.error('❌ [INTERVIEWER] Socket connect_error:', err);
        setConnectionStatus('Connection error');
      });

      socket.on('sync-panel', ({ panel }) => {
        console.log(`📡 [INTERVIEWER] Syncing panel visibility: ${panel}`);
        togglePanel(panel, true);
      });

      if (cancelled || cancelledRef.current) {
        console.warn('⚠️ [INTERVIEWER] initializeConnection cancelled (Stage 1)');
        socket.disconnect();
        return;
      }

      // --- LiveKit Connection ---
      console.log('🎫 [INTERVIEWER] Fetching LiveKit token from:', `${API_BASE}/api/auth/livekit-token`);
      const tokenRes = await fetch(`${API_BASE}/api/auth/livekit-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, userName: interviewerName || 'Interviewer', role: 'interviewer' }),
      });
      
      if (!tokenRes.ok) {
        const errorText = await tokenRes.text();
        throw new Error(`Server returned ${tokenRes.status}: ${errorText}`);
      }

      const tokenData = await tokenRes.json();
      console.log('🎫 [INTERVIEWER] Token received, success:', tokenData.success);

      if (cancelled || cancelledRef.current) {
        console.warn('⚠️ [INTERVIEWER] initializeConnection cancelled (Stage 2)');
        socket.disconnect();
        return;
      }

      if (!tokenData.success || !tokenData.token || !tokenData.serverUrl) {
        throw new Error('Failed to get LiveKit token payload');
      }

      console.log('📹 [INTERVIEWER] Establishing LiveKit connection to:', tokenData.serverUrl);
      const room = new LivekitClient.Room({ adaptiveStream: true, dynacast: true });
      livekitRoomRef.current = room;

      room.on(LivekitClient.RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('📥 [INTERVIEWER] Track subscribed:', track.kind, 'from:', participant.identity);

        if (publication?.source === LivekitClient.Track.Source.ScreenShare && !participant.identity.startsWith('interviewer-')) {
          setCandidateScreenShareTrack(track);
        }

        if (track.kind === 'video') {
          if (participant.identity.startsWith('interviewer-')) {
            setRemoteInterviewers((prev) => {
              if (prev.find((p) => p.identity === participant.identity)) return prev;
              return [...prev, participant];
            });
          } else {
            console.log('👤 [INTERVIEWER] Candidate video track received');
            setCandidateParticipantName(participant.name);
            setCandidateIdentity(participant.identity);
            setCandidateParticipant(participant);
            // Store track so the useEffect can attach it once the video element mounts
            candidateVideoTrackRef.current = track;
            // Also try attaching immediately in case remoteVideoRef is already mounted
            if (remoteVideoRef.current) {
              console.log('📺 [INTERVIEWER] Attaching candidate video to remoteVideoRef (immediate)');
              track.attach(remoteVideoRef.current);
            } else {
              console.log('📺 [INTERVIEWER] remoteVideoRef not ready yet — will attach via useEffect');
            }
          }
          setConnectionStatus('✅ Video connected!');
        } else if (track.kind === 'audio') {
          const element = track.attach();
          document.body.appendChild(element);
        }
      });


      room.on(LivekitClient.RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log('📤 [INTERVIEWER] Track unsubscribed:', track.kind);
        if (publication?.source === LivekitClient.Track.Source.ScreenShare && participant && !participant.identity.startsWith('interviewer-')) {
          setCandidateScreenShareTrack(null);
        }
        track.detach();
      });

      room.on(LivekitClient.RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('🚪 [INTERVIEWER] Participant disconnected:', participant.identity);
        setRemoteInterviewers((prev) => prev.filter((p) => p.identity !== participant.identity));
      });

      room.on(LivekitClient.RoomEvent.Disconnected, () => {
        console.warn('⚠️ [INTERVIEWER] Disconnected from LiveKit room');
      });

      room.on(LivekitClient.RoomEvent.TrackMuted, (publication, participant) => {
        if (publication.kind === 'audio') {
          setMutedParticipants(prev => { const s = new Set(prev); s.add(participant.identity); return s; });
        }
      });

      room.on(LivekitClient.RoomEvent.TrackUnmuted, (publication, participant) => {
        if (publication.kind === 'audio') {
          setMutedParticipants(prev => { const s = new Set(prev); s.delete(participant.identity); return s; });
        }
      });

      console.log('🔗 [INTERVIEWER] Calling room.connect...');
      await room.connect(tokenData.serverUrl, tokenData.token);

      if (cancelled || cancelledRef.current) {
        console.warn('⚠️ [INTERVIEWER] initializeConnection cancelled (Stage 3)');
        room.disconnect();
        socket.disconnect();
        return;
      }

      console.log('✅ [INTERVIEWER] Connected to LiveKit room:', room.name);

      console.log('📸 [INTERVIEWER] Enabling local camera/mic...');
      await room.localParticipant.enableCameraAndMicrophone();

      if (cancelled || cancelledRef.current) {
        console.warn('⚠️ [INTERVIEWER] initializeConnection cancelled (Stage 4)');
        room.disconnect();
        socket.disconnect();
        return;
      }

      const cameraPub = room.localParticipant.getTrackPublication(LivekitClient.Track.Source.Camera);
      if (cameraPub && cameraPub.videoTrack) {
         if (localVideoRef.current) {
            console.log('📹 [INTERVIEWER] Attaching local camera to localVideoRef');
            cameraPub.videoTrack.attach(localVideoRef.current);
         } else {
            console.warn('📹 [INTERVIEWER] localVideoRef.current is missing during attachment');
         }
      } else {
        console.warn('📹 [INTERVIEWER] No local camera publication found');
      }

      const localTracks = [];
      room.localParticipant.getTracks().forEach((pub) => {
        if (pub.track && pub.track.mediaStreamTrack) localTracks.push(pub.track.mediaStreamTrack);
      });
      if (localTracks.length > 0) {
        localStreamRef.current = new MediaStream(localTracks);
      }

      console.log('🏁 [INTERVIEWER] Connection initialization complete - Ready');
      setConnectionStatus('Ready');
    } catch (error) {
      if (!cancelledRef.current) {
        console.error('❌ [INTERVIEWER] Connection initialization failed:', error);
        setConnectionStatus('Error: ' + (error.message || error));
      }
    }
  };

  // ---------- Cleanup ----------
  const cleanupConnection = () => {
    console.log('🧹 cleanupConnection()');
    try { if (livekitRoomRef.current) { livekitRoomRef.current.disconnect(); livekitRoomRef.current = null; } } catch (e) { }
    try { if (localStreamRef.current) { localStreamRef.current.getTracks().forEach((t) => t.stop()); localStreamRef.current = null; } } catch (e) { }
    try { if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; } } catch (e) { }
  };

  useEffect(() => {
    if (step !== 'meeting') return;
    if (!candidateConnected) return;
    if (screenshotStartedRef.current) return;
    if (!localVideoRef.current) return;

    screenshotStartedRef.current = true;
    console.log('📸 Starting interviewer screenshot capture (candidate connected)...');
    startScreenshotCapture(localVideoRef.current);
  }, [step, candidateConnected, startScreenshotCapture]);

  useEffect(() => {
    if (step !== 'meeting') return;

    const checkInterviewStatus = async () => {
      if (hasRedirectedToResultsRef.current) return;
      try {
        const response = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/status`);
        if (!response.ok) return;
        const data = await response.json();
        if (data.status === 'COMPLETED') {
          console.log('✅ Interview already completed. Redirecting interviewer to results...');
          cleanupConnection();
          redirectToResultsOnce();
        }
      } catch (error) {
        console.warn('⚠️ Failed to poll interview status for interviewer redirect:', error);
      }
    };

    checkInterviewStatus();
    const poller = window.setInterval(checkInterviewStatus, 5000);

    return () => window.clearInterval(poller);
  }, [step, roomId]);

  // ---------- Alert helpers ----------
  const clearAlert = (alertId) => setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  const clearAllAlerts = () => setAlerts([]);

  // ---------- Render ----------
  if (isCheckingLink) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: FONT_STACK,
          backgroundColor: 'var(--bg-page-flat)',
          padding: 20,
        }}
      >
        <GlobalFontImport />
        <div
          style={{
            maxWidth: 380,
            width: '100%',
            backgroundColor: 'var(--bg-card)',
            borderRadius: 18,
            padding: '40px 36px',
            boxShadow: '0 12px 40px rgba(15,23,42,0.08)',
            border: '1px solid var(--border-color)',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px',
          }}
        >
          <div style={{
            width: '44px', height: '44px', borderRadius: '50%',
            border: '3px solid var(--border-color)',
            borderTop: '3px solid #2563eb',
            borderRight: '3px solid #7c3aed',
            animation: 'spinChecking 0.85s cubic-bezier(0.5, 0, 0.5, 1) infinite',
          }} />
          <span style={{ fontWeight: 600, fontSize: '15px', letterSpacing: '-0.01em' }}>
            Verifying interview link...
          </span>
        </div>
        <style>{`@keyframes spinChecking { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isLinkExpired) {
    return <LinkExpiredView />;
  }

  const cleaningOverlay = isCleaning ? (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--bg-overlay-light)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <div
        style={{
          width: '46px',
          height: '46px',
          borderRadius: '50%',
          border: '4px solid var(--border-light)',
          borderTop: '4px solid #2563eb',
          animation: 'copilotSpin 0.9s linear infinite',
        }}
      />
      <h2 style={{ color: '#1976d2', margin: 0 }}>Ending Interview...</h2>
      <p style={{ color: 'var(--text-tertiary)', margin: 0, fontSize: '14px' }}>
        Saving transcript and generating final report. Please wait.
      </p>
    </div>
  ) : null;

  // name entry point
  if (step === 'nameEntry') {


    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FONT_STACK,
        backgroundColor: 'var(--bg-page-flat)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <GlobalFontImport />

        {/* Ambient background mesh — quiet signature for the entry screen */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: '-12%', left: '-8%', width: '46%', height: '46%',
            background: 'radial-gradient(circle, rgba(37,99,235,0.16), transparent 70%)', filter: 'blur(10px)',
          }} />
          <div style={{
            position: 'absolute', bottom: '-16%', right: '-10%', width: '52%', height: '52%',
            background: 'radial-gradient(circle, rgba(124,58,237,0.14), transparent 70%)', filter: 'blur(10px)',
          }} />
        </div>

        <Navbar isLoggedIn showSchedule={false} showCalendar={false} showProfile={false} showLogout={false} />
        {/* ✅ Loader shows even on name entry screen if somehow triggered */}
        {isCleaning && <LoadingOverlay />}

        <div style={{
          flex: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            maxWidth: 480, width: '100%', backgroundColor: 'var(--bg-card)',
            borderRadius: 22, padding: '44px 40px',
            boxShadow: '0 24px 64px rgba(15,23,42,0.10)',
            border: '1px solid var(--border-color)',
          }}>
            <div style={{
              width: '54px', height: '54px', borderRadius: '16px', marginBottom: '20px',
              background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 10px 24px rgba(37,99,235,0.3)',
            }}>
              <svg viewBox="0 0 24 24" width="26" height="26" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z"></path><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
            </div>
            <h2 style={{ marginBottom: 6, fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Join as Interviewer
            </h2>
            <p style={{ marginTop: 0, marginBottom: 26, fontSize: '14.5px', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              Tell us who's running this session before we connect the room.
            </p>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '6px', letterSpacing: '0.01em' }}>
              FULL NAME
            </label>
            <input
              type="text"
              placeholder="e.g. Priya Sharma"
              value={interviewerName}
              onChange={(e) => setInterviewerName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              autoFocus
              className="iv-input"
              style={{
                width: '100%',
                padding: '13px 14px',
                fontSize: 15,
                borderRadius: 10,
                border: '1.5px solid var(--border-input)',
                marginBottom: 16,
                boxSizing: 'border-box',
                background: 'var(--bg-input)',
                color: 'var(--text-primary)',
                fontFamily: FONT_STACK,
                outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
            />
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '6px', letterSpacing: '0.01em' }}>
              EMAIL <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
            </label>
            <input
              type="email"
              placeholder="you@company.com"
              value={interviewerEmail}
              onChange={(e) => setInterviewerEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              className="iv-input"
              style={{
                width: '100%', padding: '13px 14px', fontSize: 15, borderRadius: 10,
                border: '1.5px solid var(--border-input)', marginBottom: 26, boxSizing: 'border-box',
                background: 'var(--bg-input)', color: 'var(--text-primary)',
                fontFamily: FONT_STACK, outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
            />
            <button
              onClick={handleNameSubmit}
              disabled={!interviewerName.trim()}
              style={{
                width: '100%', padding: '14px', background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
                color: 'white', fontSize: 15, fontWeight: 600, border: 'none', borderRadius: 12,
                cursor: interviewerName.trim() ? 'pointer' : 'not-allowed',
                opacity: interviewerName.trim() ? 1 : 0.5,
                transition: 'opacity 0.25s ease, transform 0.15s ease, box-shadow 0.15s ease',
                boxShadow: interviewerName.trim() ? '0 10px 24px rgba(37,99,235,0.32)' : 'none',
                fontFamily: FONT_STACK,
                letterSpacing: '-0.01em',
              }}
            >
              Continue to Room →
            </button>
          </div>
        </div>
        {/* <Footer /> */}
        <style>{`
          .iv-input:focus { border-color: #2563eb !important; box-shadow: 0 0 0 4px rgba(37,99,235,0.12); }
          .iv-input::placeholder { color: var(--text-faint); }
        `}</style>
      </div>
    );
  }

  // ==========================================================
  // RENDER: Meeting Screen
  // ==========================================================
  return (
    <div style={{
      fontFamily: FONT_STACK,
      background: 'var(--bg-page-meeting)',
      minHeight: '100vh',
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <GlobalFontImport />

      {/* Ambient background mesh — quiet signature behind the call surface */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-10%', left: '-6%', width: '38%', height: '38%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.10), transparent 70%)', filter: 'blur(16px)',
        }} />
        <div style={{
          position: 'absolute', bottom: '-14%', right: '-8%', width: '42%', height: '42%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.09), transparent 70%)', filter: 'blur(16px)',
        }} />
      </div>

      {/* Full Screen Loader Overlay */}
      {isCleaning && <LoadingOverlay />}

      {/* Custom Confirm Modal */}
      {showConfirmModal && (
        <ConfirmModal
          message="Are you sure you want to end the interview? This will generate the AI report and redirect to results."
          onConfirm={handleConfirmEnd}
          onCancel={handleConfirmCancel}
        />
      )}

      {/* Main Video Area (Shrinks when sidebar is open) */}
      <div style={{
        width: isSidebarOpen ? 'calc(100% - 380px)' : '100%',
        height: '100vh',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        paddingBottom: '80px', // Space for control bar
        boxSizing: 'border-box',
        gap: '24px'
      }}>
        {/* HUGE REMOTE VIDEO (Candidate) */}
        <div style={{
          width: activePanel ? '35%' : '100%',
          height: '100%',
          flexShrink: 0,
          backgroundColor: '#000',
          borderRadius: '20px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: candidateConnected
            ? '0 16px 48px rgba(15,23,42,0.22), 0 0 0 1.5px rgba(37,99,235,0.35)'
            : '0 16px 48px rgba(15,23,42,0.16)',
          border: '1px solid rgba(255,255,255,0.6)',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease'
        }}>
          <video
            ref={(el) => {
              remoteVideoRef.current = el;
              if (el) {
                // Prefer the stored track ref (most reliable) — fallback to publication lookup
                if (candidateVideoTrackRef.current) {
                  console.log('📺 [INTERVIEWER] Attaching candidate video track via ref callback');
                  candidateVideoTrackRef.current.attach(el);
                } else if (candidateParticipant) {
                  const videoPub = Array.from(candidateParticipant.trackPublications.values()).find(p => p.kind === 'video');
                  if (videoPub && videoPub.videoTrack) videoPub.videoTrack.attach(el);
                }
              }
            }}
            autoPlay
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* Bottom gradient wash for label legibility */}
          <div aria-hidden="true" style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: '110px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: '16px', left: '16px', color: 'var(--text-video-label)',
            backgroundColor: 'var(--bg-video-label)', padding: '7px 14px',
            borderRadius: '20px', fontSize: '13px', fontWeight: '600',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', gap: '8px',
            backdropFilter: 'blur(6px)',
          }}>
            {candidateConnected && (
              <span style={{
                width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#22c55e',
                boxShadow: '0 0 0 0 rgba(34,197,94,0.6)', animation: 'liveDotPulse 1.8s ease-out infinite', flexShrink: 0,
              }} />
            )}
            {candidateIdentity && mutedParticipants.has(candidateIdentity) && (
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="#666" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
            )}
            {candidateConnected ? (candidateParticipantName ? `${candidateParticipantName} (Candidate)` : 'Candidate') : 'Waiting for Candidate...'}
          </div>

          {/* SMALL VIDEO OVERLAYS (Interviewers + You) */}
          <div style={{
            position: 'absolute', top: '16px', right: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px', zIndex: 25,
          }}>
            {remoteInterviewers.map((participant) => (
              <div key={participant.identity} style={{
                width: '200px', height: '120px', backgroundColor: '#000',
                borderRadius: '14px', overflow: 'hidden',
                boxShadow: '0 10px 28px rgba(0,0,0,0.22)', position: 'relative',
                border: '2px solid rgba(255,255,255,0.9)'
              }}>
                <video
                  autoPlay
                  playsInline
                  ref={(el) => {
                    if (el) {
                      const videoPub = Array.from(participant.trackPublications.values()).find(p => p.kind === 'video');
                      if (videoPub && videoPub.videoTrack) videoPub.videoTrack.attach(el);
                    }
                  }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', bottom: '6px', left: '6px', color: 'var(--text-video-label)',
                  backgroundColor: 'var(--bg-video-label)', padding: '4px 8px',
                  borderRadius: '8px', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center'
                }}>
                  {mutedParticipants.has(participant.identity) && (
                    <svg viewBox="0 0 24 24" width="12" height="12" stroke="#666" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                      <line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                  )}
                  {participant.name ? `${participant.name} (Interviewer)` : 'Interviewer'}
                </div>
              </div>
            ))}

            {/* You / Local Interviewer */}
            <div style={{
              width: candidateScreenShareTrack ? '140px' : '200px',
              height: candidateScreenShareTrack ? '80px' : '120px',
              backgroundColor: '#000',
              borderRadius: '14px', overflow: 'hidden',
              boxShadow: '0 10px 28px rgba(0,0,0,0.22)', position: 'relative',
              border: '2px solid rgba(255,255,255,0.9)',
              transition: 'all 0.3s ease'
            }}>
              <video
                ref={(el) => {
                  localVideoRef.current = el;
                  if (el && livekitRoomRef.current && livekitRoomRef.current.localParticipant) {
                    const cameraPub = livekitRoomRef.current.localParticipant.getTrackPublication(LivekitClient.Track.Source.Camera);
                    if (cameraPub && cameraPub.videoTrack) cameraPub.videoTrack.attach(el);
                  }
                }}
                autoPlay
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scaleX(-1)' }}
              />
              <div style={{ position: 'absolute', bottom: '6px', left: '6px', color: 'var(--text-video-label)', backgroundColor: 'var(--bg-video-label)', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                {!isMicEnabled && (
                  <svg viewBox="0 0 24 24" width="12" height="12" stroke="#666" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                    <line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                )}
                You
              </div>
            </div>
          </div>
        </div>

        {/* Top Info Overlay */}
        <div style={{
          position: 'absolute', top: '32px', left: '32px',
          display: 'flex', alignItems: 'center', gap: '10px', zIndex: 10
        }}>
          <div style={{
            padding: '9px 16px', backgroundColor: 'var(--bg-info-chip)',
            backdropFilter: 'blur(10px)', color: 'var(--text-info-chip)', borderRadius: '12px',
            fontSize: '13.5px', fontWeight: '600', border: '1px solid var(--border-control)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.06)', letterSpacing: '-0.01em',
            display: 'flex', alignItems: 'center', gap: '7px',
          }}>
            <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.65 }}>
              <rect x="3" y="3" width="18" height="18" rx="3"></rect><circle cx="9" cy="9" r="1.5"></circle><path d="M21 15l-5-5L5 21"></path>
            </svg>
            Room {roomId?.substring(0, 8)}...
          </div>
          <div style={{
            padding: '9px 16px',
            backgroundColor: candidateConnected ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.14)',
            backdropFilter: 'blur(10px)',
            color: candidateConnected ? '#15803d' : '#b45309',
            borderRadius: '12px',
            fontSize: '13.5px', fontWeight: '600',
            border: `1px solid ${candidateConnected ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.3)'}`,
            boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
            display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '-0.01em',
          }}>
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%',
              backgroundColor: candidateConnected ? '#22c55e' : '#f59e0b',
              animation: candidateConnected ? 'liveDotPulse 1.8s ease-out infinite' : 'none',
              flexShrink: 0,
            }} />
            {candidateConnected ? 'Candidate connected' : 'Waiting for candidate...'}
          </div>
        </div>


        {/* Floating Control Bar */}
        <div style={{
          position: 'absolute',
          bottom: '28px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 20px',
          backgroundColor: 'var(--bg-control-bar)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 12px 40px rgba(15,23,42,0.14)',
          border: '1px solid var(--border-control)',
          zIndex: 20
        }}>
          <button
            onClick={toggleMic}
            title={isMicEnabled ? "Turn Off Microphone" : "Turn On Microphone"}
            className="iv-ctrl-btn"
            style={{
              width: '48px', height: '48px', borderRadius: '16px',
              backgroundColor: isMicEnabled ? 'var(--bg-control-btn)' : '#ea4335',
              color: isMicEnabled ? ACCENT.mic : '#ffffff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease',
              border: isMicEnabled ? '1px solid rgba(37,99,235,0.18)' : 'none',
              boxShadow: isMicEnabled ? 'none' : '0 6px 16px rgba(234,67,53,0.35)'
            }}
          >
            {isMicEnabled ? (
              <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            )}
          </button>

          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            title="Toggle Alerts"
            className="iv-ctrl-btn"
            style={{
              height: '48px', padding: '0 18px', borderRadius: '16px',
              backgroundColor: isSidebarOpen ? 'rgba(37,99,235,0.12)' : 'var(--bg-control-btn)',
              color: isSidebarOpen ? ACCENT.mic : 'var(--text-tertiary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.15s ease', position: 'relative', fontFamily: FONT_STACK,
              border: isSidebarOpen ? '1px solid rgba(37,99,235,0.3)' : '1px solid var(--border-color)'
            }}
          >
            <span>🔔 Alerts</span>
            {alerts.length > 0 && !isSidebarOpen && (
              <span style={{
                backgroundColor: '#ea4335', color: 'white', borderRadius: '10px',
                padding: '2px 7px', fontSize: '11px', fontWeight: '700',
                boxShadow: '0 2px 6px rgba(234,67,53,0.4)',
              }}>
                {alerts.length}
              </span>
            )}
          </button>

          <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--divider-color)', margin: '0 6px' }}></div>

          {/* ====== WHITEBOARD BUTTON ====== */}
          <button
            onClick={() => togglePanel('whiteboard')}
            title="Toggle Whiteboard"
            className="iv-ctrl-btn"
            style={{
              height: '48px', padding: '0 18px', borderRadius: '16px',
              backgroundColor: activePanel === 'whiteboard' ? 'rgba(124,58,237,0.12)' : 'var(--bg-control-btn)',
              color: activePanel === 'whiteboard' ? ACCENT.whiteboard : 'var(--text-tertiary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.15s ease', fontFamily: FONT_STACK,
              border: activePanel === 'whiteboard' ? '1px solid rgba(124,58,237,0.3)' : '1px solid var(--border-color)'
            }}
          >
            🎨 Whiteboard
          </button>

          {/* ====== IDE BUTTON ====== */}
          <button
            onClick={() => togglePanel('ide')}
            title="Toggle Code Editor"
            className="iv-ctrl-btn"
            style={{
              height: '48px', padding: '0 18px', borderRadius: '16px',
              backgroundColor: activePanel === 'ide' ? 'rgba(22,163,74,0.12)' : 'var(--bg-control-btn)',
              color: activePanel === 'ide' ? ACCENT.ide : 'var(--text-tertiary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.15s ease', fontFamily: FONT_STACK,
              border: activePanel === 'ide' ? '1px solid rgba(22,163,74,0.3)' : '1px solid var(--border-color)'
            }}
          >
            💻 Code Editor
          </button>

          {/* ====== REPLAY BUTTON ====== */}
          <button
            onClick={() => togglePanel('playback')}
            title="Toggle Replay Viewer"
            className="iv-ctrl-btn"
            style={{
              height: '48px', padding: '0 18px', borderRadius: '16px',
              backgroundColor: activePanel === 'playback' ? 'rgba(217,119,6,0.12)' : 'var(--bg-control-btn)',
              color: activePanel === 'playback' ? ACCENT.replay : 'var(--text-tertiary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.15s ease', fontFamily: FONT_STACK,
              border: activePanel === 'playback' ? '1px solid rgba(217,119,6,0.3)' : '1px solid var(--border-color)'
            }}
          >
            🎬 Replay
          </button>

          <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--divider-color)', margin: '0 6px' }}></div>

          <button
            onClick={handleEndInterviewClick}
            title="End Call"
            className="iv-ctrl-btn iv-end-btn"
            style={{
              width: '48px', height: '48px', borderRadius: '16px',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px',
              boxShadow: '0 8px 20px rgba(220,38,38,0.4)'
            }}
          >
            📞
          </button>
        </div>
        {/* ====== COLLABORATIVE PANELS OVERLAY ====== */}
        {activePanel && (
          <div style={{
            flex: 1,
            height: '100%',
            position: 'relative',
            zIndex: 15,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideUpPanel 0.3s ease-out',
            boxShadow: '0 16px 48px rgba(15,23,42,0.28)',
            borderRadius: '20px',
            overflow: 'hidden',
            border: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-card)'
          }}>
            {showWhiteboard && socketRef.current && (
              <CollaborativeWhiteboard
                socket={socketRef.current}
                roomId={roomId}
                userName={interviewerName || userName || 'Interviewer'}
                role="interviewer"
                theme="light"
                onClose={() => togglePanel('whiteboard')}
              />
            )}
            {showIDE && socketRef.current && (
              <CollaborativeIDE
                socket={socketRef.current}
                roomId={roomId}
                userName={interviewerName || userName || 'Interviewer'}
                role="interviewer"
                onClose={() => togglePanel('ide')}
              />
            )}
            {showPlayback && (
              <PlaybackViewer
                roomId={roomId}
                onClose={() => togglePanel('playback')}
              />
            )}
          </div>
        )}
      </div>

      {/* RIGHT SIDEBAR: Monitoring Alerts */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '380px',
        height: '100vh',
        backgroundColor: 'var(--bg-sidebar)',
        transform: isSidebarOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '-8px 0 32px rgba(15,23,42,0.16)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 30
      }}>
        {/* Sidebar Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '22px 24px', borderBottom: '1px solid var(--border-color)'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', color: 'var(--text-primary)', fontWeight: 700, letterSpacing: '-0.01em' }}>
              Monitoring Alerts
            </h2>
            <span style={{ fontSize: '12.5px', color: 'var(--text-tertiary)' }}>
              {alerts.length} {alerts.length === 1 ? 'event' : 'events'} this session
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {alerts.length > 0 && (
              <button
                onClick={clearAllAlerts}
                className="iv-clear-btn"
                style={{
                  padding: '7px 14px', backgroundColor: 'transparent',
                  color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT_STACK,
                  transition: 'all 0.15s ease',
                }}
              >
                Clear all
              </button>
            )}
            <button
              onClick={() => setIsSidebarOpen(false)}
              style={{
                width: '32px', height: '32px', borderRadius: '10px',
                backgroundColor: 'var(--bg-control-btn)', border: '1px solid var(--border-color)', cursor: 'pointer',
                fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)', transition: 'all 0.15s ease',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Alerts Content */}
        <div className="iv-scroll" style={{
          flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: 'var(--bg-sidebar-content)'
        }}>
          {alerts.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '70px' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 18px',
                background: 'linear-gradient(145deg, rgba(37,99,235,0.10), rgba(124,58,237,0.06))',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px',
                border: '1px solid var(--border-color)',
              }}>
                🛡️
              </div>
              <p style={{ fontSize: '14px', lineHeight: 1.6, margin: 0 }}>No alerts yet.<br />Monitoring is active.</p>
            </div>
          ) : (
            alerts.map((alert) => {
              const severityColor = alert.severity === 'critical' ? '#dc2626'
                : alert.severity === 'high' ? '#d97706'
                  : alert.severity === 'medium' ? '#d97706'
                    : '#2563eb';
              return (
              <div
                key={alert.id}
                className="iv-alert-card"
                style={{
                  display: 'flex', marginBottom: '14px', borderRadius: '12px',
                  border: `1px solid ${alert.severity === 'critical' ? '#fad2cf'
                      : alert.severity === 'high' ? '#fce8b2'
                        : alert.severity === 'medium' ? '#fce8b2'
                          : '#d2e3fc'
                    }`,
                  overflow: 'hidden',
                  backgroundColor:
                    alert.severity === 'critical' ? '#fce8e6' // Light red
                      : alert.severity === 'high' ? '#fef7e0'   // Light yellow/orange
                        : alert.severity === 'medium' ? '#fef7e0'
                          : '#e8f0fe',                              // Light blue
                  boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                <div style={{ width: '4px', flexShrink: 0, backgroundColor: severityColor }} />
                <div style={{ padding: '14px 16px', flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary)', fontSize: '13.5px', letterSpacing: '-0.01em' }}>
                      {alert.type === 'AI_DETECTION_RESULT' && '🤖 AI Content Check'}
                      {alert.type === 'AI_SUGGESTION' && '💡 AI Suggestion'}
                      {alert.type === 'LIP_SYNC_RESULT' && '🎙️ Lip-Sync Status'}
                      {alert.type === 'LOOKING_AWAY' && '👁️ Smart Eye Tracker'}
                      {alert.type === 'TAB_SWITCHED' && '⚠️ Tab Switch'}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '10px', lineHeight: 1.45 }}>
                      {alert.message}
                    </div>

                    {alert.type === 'AI_SUGGESTION' && (
                      <div style={{
                        marginBottom: '10px', padding: '12px',
                        backgroundColor: '#e8f0fe', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.05)', color: '#174ea6', fontWeight: '500',
                      }}>
                        <span style={{fontWeight: 'normal', color: '#1967d2', display: 'block'}}>{alert.suggestedQuestion}</span>
                      </div>
                    )}

                    {alert.aiData && (
                      <div style={{
                        fontSize: '12px', color: '#3c4043', backgroundColor: 'rgba(255,255,255,0.7)',
                        padding: '12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        {alert.aiData.suggestedQuestion && (
                          <div style={{
                            marginBottom: '10px', padding: '8px 12px',
                            backgroundColor: '#e8f0fe', borderRadius: '6px', color: '#174ea6', fontWeight: '500',
                          }}>
                            💡 AI Suggestion:<br />
                            <span style={{ fontWeight: 'normal', color: '#1967d2', display: 'block', marginTop: '4px' }}>{alert.aiData.suggestedQuestion}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontWeight: '500' }}>
                          <span style={{ color: alert.aiData.aiScore > 70 ? '#d93025' : alert.aiData.aiScore > 50 ? '#f29900' : '#1e8e3e' }}>
                            Score: {alert.aiData.aiScore}%
                          </span>
                          {/* <span style={{color: '#5f6368'}}>Model: {alert.aiData.method || alert.aiData.detectionMethod}</span> */}
                        </div>
                        {alert.aiData.geminiReason && (
                          <div style={{
                            marginTop: '8px', padding: '8px 12px',
                            backgroundColor: '#fef7e0', borderRadius: '6px', color: '#b06000', fontSize: '12px',
                          }}>
                            <strong style={{ color: '#e27200' }}>Reasoning:</strong> {alert.aiData.geminiReason}
                          </div>
                        )}
                        {alert.aiData.textAnalyzed && (
                          <div style={{
                            marginTop: '10px', fontStyle: 'italic', color: '#5f6368',
                            borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '8px',
                          }}>
                            "{alert.aiData.textAnalyzed.substring(0, 80)}..."
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '10px' }}>
                      {new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <button
                    onClick={() => clearAlert(alert.id)}
                    style={{
                      marginLeft: '12px', padding: '4px', borderRadius: '6px',
                      backgroundColor: 'transparent', border: 'none',
                      cursor: 'pointer', fontSize: '14px', color: '#5f6368',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    ✕
                  </button>
                </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        .iv-ctrl-btn:hover { transform: translateY(-2px); filter: brightness(0.97); }
        .iv-ctrl-btn:active { transform: translateY(0); }
        .iv-end-btn:hover { box-shadow: 0 10px 26px rgba(220,38,38,0.5) !important; }
        .iv-clear-btn:hover { background-color: rgba(220,38,38,0.08) !important; }
        .iv-alert-card:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(15,23,42,0.10); }
        .iv-scroll::-webkit-scrollbar { width: 8px; }
        .iv-scroll::-webkit-scrollbar-thumb { background: rgba(100,116,139,0.25); border-radius: 8px; }
        .iv-scroll::-webkit-scrollbar-track { background: transparent; }
        button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
        @keyframes slideUpPanel {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes liveDotPulse {
          0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
          70% { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
          100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.001s !important; transition-duration: 0.001s !important; }
        }
      `}</style>
    </div>
  );
};

export default InterviewerView;